import * as amplitude from '@amplitude/analytics-browser';

interface UserProperties {
  userId?: string;
  email?: string;
  role?: string;
  [key: string]: any;
}

/**
 * Analytics service for tracking user events with Amplitude
 *
 * Features:
 * - Development mode: logs to console when API key is missing
 * - Production mode: sends events to Amplitude
 * - User identification and properties management
 * - Session tracking
 */
class Analytics {
  private initialized = false;
  private isNoApiKey = false;

  /**
   * Initialize Amplitude analytics
   * Call once at app startup
   */
  init() {
    const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;

    if (!apiKey) {
      this.isNoApiKey = true;
      console.log(
        '%c[Analytics] %cNo API Key - events will be logged to console only',
        'color: #2563eb; font-weight: bold',
        'color: #6b7280'
      );
      this.initialized = true;
      return;
    }

    try {
      amplitude.init(apiKey, {
        // Auto-track sessions
        defaultTracking: {
          sessions: true,
          pageViews: false, // We'll track manually for better control
          formInteractions: false,
          fileDownloads: true,
        },
        // Add environment to all events automatically
        ingestionMetadata: {
          sourceName: import.meta.env.DEV ? 'development' : 'production',
          sourceVersion: '1.0.0',
        },
      });

      // Set environment user property immediately
      const identify = new amplitude.Identify();
      identify.set(
        'environment',
        import.meta.env.DEV ? 'development' : 'production'
      );
      amplitude.identify(identify);

      this.initialized = true;
      this.isNoApiKey = false;
      console.log(
        '%c[Analytics] %cInitialized successfully %c(Env: ' +
          (import.meta.env.DEV ? 'Development' : 'Production') +
          ')',
        'color: #2563eb; font-weight: bold',
        'color: #16a34a',
        'color: #6b7280'
      );
    } catch (error) {
      console.error('[Analytics] Initialization failed:', error);
      this.isNoApiKey = true;
      this.initialized = true;
    }
  }

  /**
   * Identify a user and set user properties
   * Call after successful login
   */
  identifyUser(userId: string, properties?: UserProperties) {
    if (!this.initialized) {
      console.warn('[Analytics] Not initialized. Call init() first.');
      return;
    }

    // Log in dev mode or if no API key
    if (this.isNoApiKey || import.meta.env.DEV) {
      console.log(
        '%c[Analytics] %cIdentify User:',
        'color: #2563eb; font-weight: bold',
        'color: #6b7280',
        { userId, properties }
      );
    }

    if (!this.isNoApiKey) {
      amplitude.setUserId(userId);

      if (properties) {
        const identify = new amplitude.Identify();
        Object.entries(properties).forEach(([key, value]) => {
          identify.set(key, value);
        });
        amplitude.identify(identify);
      }
    }
  }

  /**
   * Reset user identity
   * Call on logout
   */
  resetUser() {
    if (!this.initialized) return;

    if (this.isNoApiKey || import.meta.env.DEV) {
      console.log(
        '%c[Analytics] %cReset User',
        'color: #2563eb; font-weight: bold',
        'color: #6b7280'
      );
    }

    if (!this.isNoApiKey) {
      amplitude.reset();
    }
  }

  /**
   * Track an event with optional properties
   */
  track(eventName: string, eventProperties?: Record<string, any>) {
    if (!this.initialized) {
      console.warn('[Analytics] Not initialized. Call init() first.');
      return;
    }

    if (this.isNoApiKey || import.meta.env.DEV) {
      console.log(
        '%c[Analytics] %c' + eventName,
        'color: #2563eb; font-weight: bold',
        'color: #059669; font-weight: bold',
        eventProperties || {}
      );
    }

    if (!this.isNoApiKey) {
      amplitude.track(eventName, eventProperties);
    }
  }

  /**
   * Track a page view
   * Convenience method for page tracking
   */
  trackPageView(pageName: string, properties?: Record<string, any>) {
    this.track('Page Viewed', {
      page_name: pageName,
      page_url: window.location.pathname,
      page_search: window.location.search,
      ...properties,
    });
  }

  /**
   * Update user properties without changing user ID
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized) return;

    if (this.isNoApiKey || import.meta.env.DEV) {
      console.log(
        '%c[Analytics] %cSet User Properties:',
        'color: #2563eb; font-weight: bold',
        'color: #6b7280',
        properties
      );
    }

    if (!this.isNoApiKey) {
      const identify = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value);
      });
      amplitude.identify(identify);
    }
  }
}

// Singleton instance
export const analytics = new Analytics();
